import { API_BASE } from '../apiBase';
// src/billing/QuickCreateProject.js
// Inline card that creates a customer (if new) + a project in Lead status
// without leaving the screen you're on. Shared by CreateQuote and
// CreateInvoice — a verbal go-ahead often skips the quote entirely, so the
// invoice screen needs the same fast path.
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { getAddressSuggestions, geocodePlaceId } from '../lib/googlePlaces';

export default function QuickCreateProject({ seedTitle = '', projects = [], onDone, onCancel }) {
  const [form, setForm] = useState({ title: seedTitle, customerId: '', customerName: '', custSearch: '', street: '', city: '', state: 'CA', zip: '' });
  const [customers, setCustomers] = useState([]);
  const [showCustList, setShowCustList] = useState(false);
  const [newCust, setNewCust] = useState(null); // { firstName, lastName, phone, email } | null
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // job-address autocomplete (with manual fallback)
  const [addrSugs, setAddrSugs] = useState([]);
  const [addrErr, setAddrErr] = useState(null);
  const [addrOpen, setAddrOpen] = useState(false);
  const [manualAddr, setManualAddr] = useState(false);
  const addrTimer = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_BASE}/customers`, { headers: { Authorization: `Bearer ${token}` } });
        setCustomers(res.data || []);
      } catch (e) { /* list stays empty; user can still quick-create a customer */ }
    })();
  }, []);

  const handleStreet = (val) => {
    setForm(prev => ({ ...prev, street: val }));
    clearTimeout(addrTimer.current);
    if (val.trim().length < 3) { setAddrSugs([]); setAddrOpen(false); return; }
    addrTimer.current = setTimeout(async () => {
      const { predictions, error: err } = await getAddressSuggestions(val);
      setAddrErr(err); setAddrSugs(predictions); setAddrOpen(true);
    }, 350);
  };
  const pickAddress = async (p) => {
    setAddrOpen(false);
    setForm(prev => ({ ...prev, street: p.description }));
    try {
      const parts = await geocodePlaceId(p.placeId);
      setForm(prev => ({ ...prev, street: parts.street, city: parts.city, state: parts.state, zip: parts.zip }));
    } catch (e) { /* keep the typed text if parsing fails */ }
  };

  const save = async () => {
    if (!form.title.trim()) { setError('Project title is required'); return; }
    setSaving(true); setError('');
    try {
      const token = localStorage.getItem('token');
      let customerId = form.customerId;

      if (!customerId) {
        if (!newCust?.firstName?.trim()) { setError('Pick a customer or enter a first name for a new one'); setSaving(false); return; }
        const custPayload = {
          firstName: newCust.firstName.trim(), lastName: (newCust.lastName || '').trim(),
          name: `${newCust.firstName} ${newCust.lastName || ''}`.replace(/\s+/g, ' ').trim(),
          type: 'Homeowner', status: 'Lead',
          primaryPhone: (newCust.phone || '').trim(), primaryEmail: (newCust.email || '').trim(),
          billingAddress: { street: '', city: '', state: 'CA', zip: '' },
        };
        const cRes = await axios.post(`${API_BASE}/customers`, custPayload, { headers: { Authorization: `Bearer ${token}` } });
        customerId = cRes.data._id;
      }

      const maxPO = projects.length > 0 ? Math.max(...projects.map(p => parseInt(p.poNumber || '0', 10))) : 0;
      const projPayload = {
        poNumber: String(maxPO + 1).padStart(4, '0'),
        title: form.title.trim(),
        customer: customerId,
        status: 'Lead',
        billTo: 'Business',
        value: 0,
        startDate: null, endDate: null,
        projectManager: undefined,
        jobSiteAddress: { street: form.street, city: form.city, state: form.state || 'CA', zip: form.zip, country: 'USA' },
        sameAsCustomerAddress: false,
      };
      const pRes = await axios.post(`${API_BASE}/projects`, projPayload, { headers: { Authorization: token } });

      // Re-fetch so the selected project carries a populated customer
      const listRes = await axios.get(`${API_BASE}/projects`, { headers: { Authorization: token } });
      const created = (listRes.data || []).find(p => p._id === pRes.data._id) || pRes.data;
      onDone(created, listRes.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create project');
    } finally {
      setSaving(false);
    }
  };

  const field = { padding: '10px 12px', border: '1px solid #ccd0d5', borderRadius: '6px', fontSize: '14px' };
  const linkBtn = { background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '13px' };

  return (
    <div style={{ marginTop: '10px', border: '1px solid #c7d2fe', background: '#f8faff', borderRadius: '8px', padding: '16px' }}>
      <p style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: 700, color: '#4f46e5', textTransform: 'uppercase', letterSpacing: '.05em' }}>New project</p>
      <input
        value={form.title}
        onChange={(e) => setForm({ ...form, title: e.target.value })}
        placeholder="Project title * (e.g. Johnson panel upgrade)"
        style={{ ...field, width: '100%', marginBottom: '8px', boxSizing: 'border-box' }}
      />

      {/* customer: pick existing or create new */}
      {form.customerId ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '14px' }}>
          <span style={{ background: '#e0e7ff', color: '#3730a3', padding: '4px 10px', borderRadius: '999px', fontWeight: 600 }}>{form.customerName}</span>
          <button type="button" onClick={() => setForm({ ...form, customerId: '', customerName: '' })} style={linkBtn}>change</button>
        </div>
      ) : newCust ? (
        <div style={{ marginBottom: '8px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
            <input value={newCust.firstName} onChange={(e) => setNewCust({ ...newCust, firstName: e.target.value })} placeholder="First name *" style={field} />
            <input value={newCust.lastName} onChange={(e) => setNewCust({ ...newCust, lastName: e.target.value })} placeholder="Last name" style={field} />
            <input value={newCust.phone} onChange={(e) => setNewCust({ ...newCust, phone: e.target.value })} placeholder="Phone" style={field} />
            <input value={newCust.email} onChange={(e) => setNewCust({ ...newCust, email: e.target.value })} placeholder="Email" style={field} />
          </div>
          <button type="button" onClick={() => setNewCust(null)} style={{ ...linkBtn, padding: 0 }}>← back to existing customers</button>
        </div>
      ) : (
        <div style={{ position: 'relative', marginBottom: '8px' }}>
          <input
            value={form.custSearch}
            onChange={(e) => { setForm({ ...form, custSearch: e.target.value }); setShowCustList(true); }}
            onFocus={() => setShowCustList(true)}
            onBlur={() => setTimeout(() => setShowCustList(false), 250)}
            placeholder="Customer * — type to search…"
            style={{ ...field, width: '100%', boxSizing: 'border-box' }}
          />
          {showCustList && (
            <div style={{ position: 'absolute', left: 0, right: 0, top: '100%', marginTop: '4px', border: '1px solid #ccd0d5', borderRadius: '6px', maxHeight: '180px', overflowY: 'auto', background: 'white', zIndex: 30, boxShadow: '0 8px 24px rgba(0,0,0,.12)' }}>
              {customers.filter(c => (c.name || '').toLowerCase().includes(form.custSearch.toLowerCase())).slice(0, 8).map(c => (
                <div key={c._id} onClick={() => { setForm({ ...form, customerId: c._id, customerName: c.name }); setShowCustList(false); }} style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', fontSize: '14px' }}>
                  {c.name} <span style={{ color: '#9ca3af', fontSize: '12px' }}>({c.type})</span>
                </div>
              ))}
              <div onMouseDown={(e) => { e.preventDefault(); const parts = form.custSearch.trim().split(/\s+/); setNewCust({ firstName: parts[0] || '', lastName: parts.slice(1).join(' '), phone: '', email: '' }); setShowCustList(false); }}
                style={{ padding: '10px 12px', cursor: 'pointer', color: '#4f46e5', fontWeight: 600, fontSize: '14px', position: 'sticky', bottom: 0, background: 'white', borderTop: '1px solid #f0f0f0' }}>
                + New customer{form.custSearch.trim() ? ` “${form.custSearch.trim()}”` : ''}
              </div>
            </div>
          )}
        </div>
      )}

      {/* job address (optional) — Google Places search with manual fallback */}
      {manualAddr ? (
        <div style={{ marginBottom: '8px' }}>
          <input value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} placeholder="Street" style={{ ...field, width: '100%', boxSizing: 'border-box', marginBottom: '8px' }} />
          <div style={{ display: 'grid', gridTemplateColumns: '2fr .8fr 1fr', gap: '8px' }}>
            <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City" style={field} />
            <input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="State" style={field} />
            <input value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} placeholder="ZIP" style={field} />
          </div>
          <button type="button" onClick={() => setManualAddr(false)} style={{ ...linkBtn, padding: '6px 0 0' }}>← use address search instead</button>
        </div>
      ) : form.city || form.zip ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '14px' }}>
          <span style={{ background: '#ecfdf5', color: '#065f46', padding: '4px 10px', borderRadius: '999px', fontWeight: 600 }}>
            {form.street}{form.street ? ', ' : ''}{form.city}, {form.state} {form.zip}
          </span>
          <button type="button" onClick={() => setForm({ ...form, street: '', city: '', state: 'CA', zip: '' })} style={linkBtn}>change</button>
          <button type="button" onClick={() => setManualAddr(true)} style={linkBtn}>edit manually</button>
        </div>
      ) : (
        <div style={{ position: 'relative', marginBottom: '8px' }}>
          <input value={form.street} onChange={(e) => handleStreet(e.target.value)}
            onFocus={() => { if (addrSugs.length || addrErr) setAddrOpen(true); }}
            onBlur={() => setTimeout(() => setAddrOpen(false), 250)}
            placeholder="Job address (optional) — start typing to search…" style={{ ...field, width: '100%', boxSizing: 'border-box' }} />
          {addrOpen && (
            <div style={{ position: 'absolute', left: 0, right: 0, top: '100%', marginTop: '4px', border: '1px solid #ccd0d5', borderRadius: '6px', maxHeight: '190px', overflowY: 'auto', background: 'white', zIndex: 40, boxShadow: '0 8px 24px rgba(0,0,0,.12)' }}>
              {addrErr ? (
                <div style={{ padding: '10px 12px', color: '#ef4632', fontSize: '13px' }}>{addrErr}</div>
              ) : addrSugs.length === 0 ? (
                <div style={{ padding: '10px 12px', color: '#888', fontSize: '13px' }}>No matching addresses</div>
              ) : (
                addrSugs.map((p, i) => (
                  <div key={i} onClick={() => pickAddress(p)} style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', fontSize: '14px' }}>
                    {p.description}
                  </div>
                ))
              )}
              <div onMouseDown={(e) => { e.preventDefault(); setAddrOpen(false); setManualAddr(true); }}
                style={{ padding: '10px 12px', cursor: 'pointer', color: '#4f46e5', fontWeight: 600, fontSize: '13px', borderTop: '1px solid #f0f0f0', position: 'sticky', bottom: 0, background: 'white' }}>
                Enter address manually
              </div>
            </div>
          )}
          <button type="button" onClick={() => setManualAddr(true)} style={{ ...linkBtn, padding: '6px 0 0' }}>enter manually</button>
        </div>
      )}

      {error && <div style={{ color: '#ef4632', fontSize: '13px', marginBottom: '8px' }}>{error}</div>}
      <div style={{ display: 'flex', gap: '8px' }}>
        <button type="button" onClick={onCancel} style={{ flex: 1, padding: '10px', background: '#eef0f3', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
        <button type="button" onClick={save} disabled={saving} style={{ flex: 1, padding: '10px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Creating…' : 'Create & use'}
        </button>
      </div>
      <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#6b7280' }}>Creates the customer (if new) and a project in Lead status — details can be filled in later on their pages.</p>
    </div>
  );
}
